/* eslint-disable camelcase */
/* eslint-disable consistent-return */
/* eslint-disable no-param-reassign */
import * as Sentry from '@sentry/react';
import { produce } from 'immer';

import { actions as CHART_ACTIONS } from 'actionTypes/charts';
import {
  TOGGLE_OPEN_INTEREST_SELECTED_EXPIRIES,
  UPDATE_ASSET_SYMBOL,
  UPDATE_EXPIRIES,
  UPDATE_FORMATTED_EXPIRIES,
  UPDATE_OPEN_INTEREST_SELECTED_EXPIRIES,
  TOGGLE_OPEN_INTEREST_ALL_EXPIRIES,
  UPDATE_IMPLIED_VOLATILITY_SELECTED_EXPIRIES,
  UPDATE_IMPLIED_VOLATILITY_ASSET_SYMBOL,
  TOGGLE_IMPLIED_VOLATILITY_SELECTED_EXPIRY,
  UPDATE_VOLUME_STRIKE_CHART_ACTIVE_ASSET_SYMBOL,
  TOGGLE_VOLUME_STRIKE_CHART_ALL_EXPIRIES,
  TOGGLE_VOLUME_STRIKE_CHART_SELECTED_EXPIRIES,
  UPDATE_VOLUME_STRIKE_CHART_INITIAL_SELECTED_EXPIRIES,
  UPDATE_OI_CHANGE_ASSET_SYMBOL,
  UPDATE_OI_CHANGE_EXPIRIES,
  UPDATE_OI_CHANGE_TIME_PERIOD,
  TOGGLE_OI_CHANGE_EXPIRY,
  TOGGLE_OI_CHANGE_ALL_EXPIRIES,
  UPDATE_TAKER_ACTIVITY_TIME_PERIOD,
  UPDATE_TAKER_ACTIVITY_ACTIVE_ASSET,
  UPDATE_TAKER_ACTIVITY_EXPIRIES,
  TOGGLE_TAKER_ACTIVITY_ALL_EXPIRIES,
  TOGGLE_TAKER_ACTIVITY_EXPIRY,
  UPDATE_TAKER_ACTIVITY_LOADING_DATA,
  UPDATE_TAKER_ACTIVITY_ERROR,
  UPDATE_TAKER_ACTIVITY_DATA,
  UPDATE_TAKER_ACTIVITY_SOCKET_DATA,
} from 'actionTypes/optionsAnalytics';
import {
  OI_CHANGE_TIME_PERIOD_MAPPING,
  TAKER_ACTIVITY_CONTRACT_TYPE_MAPPING,
} from 'constants/optionsAnalytics';
import { forEachObjIndexed, mapObjIndexed, mergeDeepWith } from 'helpers/ramda';
import { ChartSupportedAssets, OiChartTimePeriodType } from 'types/actions/charts';
import type {
  TakerActivityFormattedData,
  TakerActivityReduxState,
  Expiries,
  ImpliedVolatilityChartReduxState,
} from 'types/optionsAnalytics';
import { SupportedAssetSymbols } from 'UIKit/DeltaAssetSymbolIcon';

/**
 * @deprecated
 * TODO: will remove this type once code get refactored completely
 */
export type SelectedExpiries = Record<string, Record<string, boolean>>;
const oiChangeInitialData = Object.fromEntries(
  Object.values(OI_CHANGE_TIME_PERIOD_MAPPING).map(period => [period, {}])
);

export interface OpenInterestChartParams {
  activeAssetSymbol: SupportedAssetSymbols;
  selectedExpiries: SelectedExpiries;
  maxPain: Record<string, number>;
}

export interface VolumeStrikeChartParams {
  activeAssetSymbol: SupportedAssetSymbols;
  expiries: Expiries;
}

export interface OiChangeData {
  [timestamp: string]: {
    [expiry: string]: {
      call: { [strike: string]: number };
      put: { [strike: string]: number };
    };
  };
}

export interface OpenInterestChangeChartParams {
  activeAssetSymbol: ChartSupportedAssets;
  expiries: Record<string, Record<string, boolean>>;
  data: OiChangeData;
  timePeriod: OiChartTimePeriodType;
}

export interface FormattedExpiries {
  [key: string]: string;
}

export interface OptionsAnalytics {
  expiries: Record<string, number[]>;
  formattedExpiries: FormattedExpiries;
  openInterestChart: OpenInterestChartParams;
  volumeStrikeChart: VolumeStrikeChartParams;
  oiChangeChart: OpenInterestChangeChartParams;
  takerActivityChart: TakerActivityReduxState;
  impliedVolatilityChart: ImpliedVolatilityChartReduxState;
}

const initialState: OptionsAnalytics = {
  expiries: {},
  formattedExpiries: {},
  openInterestChart: {
    activeAssetSymbol: 'BTC',
    selectedExpiries: {
      BTC: {},
      ETH: {},
    },
    maxPain: {},
  },
  volumeStrikeChart: {
    activeAssetSymbol: 'BTC',
    expiries: {
      BTC: {},
      ETH: {},
    },
  },
  oiChangeChart: {
    activeAssetSymbol: 'BTC',
    expiries: {
      BTC: {},
      ETH: {},
    },
    data: oiChangeInitialData,
    timePeriod: OI_CHANGE_TIME_PERIOD_MAPPING.LAST_TWELVE_HOURS as OiChartTimePeriodType,
  },
  takerActivityChart: {
    isLoading: false,
    data: {},
    isError: false,
    activeAssetSymbol: 'BTC',
    activeTimePeriod: '12h',
    expiries: {
      BTC: {},
      ETH: {},
    },
  },
  impliedVolatilityChart: {
    activeAssetSymbol: 'BTC',
    expiries: {
      BTC: {} as Expiries['asset'],
      ETH: {} as Expiries['asset'],
    },
  },
};

// eslint-disable-next-line default-param-last
const optionsAnalyticsReducer = produce((draft = initialState, action) => {
  switch (action.type) {
    case UPDATE_EXPIRIES:
      draft.expiries = action.payload;
      break;
    case UPDATE_FORMATTED_EXPIRIES:
      draft.formattedExpiries = action.payload;
      break;
    case UPDATE_ASSET_SYMBOL:
      draft.openInterestChart.activeAssetSymbol = action.payload;
      break;
    case UPDATE_OPEN_INTEREST_SELECTED_EXPIRIES:
      draft.openInterestChart.selectedExpiries = action.payload;
      break;
    case TOGGLE_OPEN_INTEREST_SELECTED_EXPIRIES:
      draft.openInterestChart.selectedExpiries[action.payload.activeAssetSymbol][
        action.payload.expiryInSeconds
      ] =
        !draft.openInterestChart.selectedExpiries[action.payload.activeAssetSymbol][
          action.payload.expiryInSeconds
        ];
      break;
    case CHART_ACTIONS.GET_OI_MAX_PAIN.SUCCESS: {
      const { selectedExpiries, activeAssetSymbol } = draft.openInterestChart;
      const activeAssetSelectedExpiries = selectedExpiries[activeAssetSymbol];
      const currentExpiry = Object.keys(activeAssetSelectedExpiries).find(
        timestamp => activeAssetSelectedExpiries[timestamp]
      );
      if (currentExpiry) {
        const { strike } = action.result.result ?? { strike: '' };
        const maxPainKey = `${currentExpiry}/${activeAssetSymbol}`;
        draft.openInterestChart.maxPain[maxPainKey] = strike;
      }
      break;
    }
    case CHART_ACTIONS.GET_OI_MAX_PAIN.FAIL: {
      Sentry.withScope(scope => {
        scope.setTag('action', 'GET_OI_MAX_PAIN.FAIL');
        Sentry.captureMessage(action.error.message);
      });
      break;
    }
    case TOGGLE_OPEN_INTEREST_ALL_EXPIRIES: {
      const activeAssetSelectedExpiries =
        draft.openInterestChart.selectedExpiries[
          draft.openInterestChart.activeAssetSymbol
        ];

      const allSelected = Object.values(activeAssetSelectedExpiries).every(
        isSelected => isSelected
      );
      Object.keys(activeAssetSelectedExpiries).forEach(expiry => {
        activeAssetSelectedExpiries[expiry] = !allSelected;
      });
      break;
    }
    case UPDATE_IMPLIED_VOLATILITY_SELECTED_EXPIRIES:
      draft.impliedVolatilityChart.expiries = action.payload;
      break;
    case UPDATE_IMPLIED_VOLATILITY_ASSET_SYMBOL:
      draft.impliedVolatilityChart.activeAssetSymbol = action.payload;
      break;
    case TOGGLE_IMPLIED_VOLATILITY_SELECTED_EXPIRY: {
      const { expiries, activeAssetSymbol } = draft.impliedVolatilityChart;
      const { payload } = action;
      draft.impliedVolatilityChart.expiries[activeAssetSymbol][payload] =
        !expiries[activeAssetSymbol][payload];
      break;
    }
    case UPDATE_VOLUME_STRIKE_CHART_INITIAL_SELECTED_EXPIRIES:
      draft.volumeStrikeChart.expiries = action.payload;
      break;
    case UPDATE_VOLUME_STRIKE_CHART_ACTIVE_ASSET_SYMBOL:
      draft.volumeStrikeChart.activeAssetSymbol = action.payload;
      break;
    case TOGGLE_VOLUME_STRIKE_CHART_ALL_EXPIRIES: {
      const activeAssetSelectedExpiries =
        draft.volumeStrikeChart.expiries[draft.volumeStrikeChart.activeAssetSymbol];

      const allSelected = Object.values(activeAssetSelectedExpiries).every(
        isSelected => isSelected
      );
      Object.keys(activeAssetSelectedExpiries).forEach(expiry => {
        activeAssetSelectedExpiries[expiry] = !allSelected;
      });
      break;
    }
    case TOGGLE_VOLUME_STRIKE_CHART_SELECTED_EXPIRIES:
      draft.volumeStrikeChart.expiries[action.payload.activeAssetSymbol][
        action.payload.expiryInSeconds
      ] =
        !draft.volumeStrikeChart.expiries[action.payload.activeAssetSymbol][
          action.payload.expiryInSeconds
        ];
      break;

    // Open Interest Change Chart cases
    case UPDATE_OI_CHANGE_ASSET_SYMBOL:
      draft.oiChangeChart.activeAssetSymbol = action.payload;
      break;
    case UPDATE_OI_CHANGE_EXPIRIES: {
      draft.oiChangeChart.expiries = action.payload;
      break;
    }
    case CHART_ACTIONS.GET_OI_CHART.SUCCESS: {
      const { data: oiChangeChartData, timePeriod } = draft.oiChangeChart;
      oiChangeChartData[timePeriod] = action.result.result;
      break;
    }
    case UPDATE_OI_CHANGE_TIME_PERIOD:
      draft.oiChangeChart.timePeriod = action.payload;
      break;
    case TOGGLE_OI_CHANGE_EXPIRY:
      draft.oiChangeChart.expiries[action.payload.activeAssetSymbol][
        action.payload.expiryInSeconds
      ] =
        !draft.oiChangeChart.expiries[action.payload.activeAssetSymbol][
          action.payload.expiryInSeconds
        ];
      break;
    case TOGGLE_OI_CHANGE_ALL_EXPIRIES: {
      const oiChangeActiveExpiries =
        draft.oiChangeChart.expiries[draft.oiChangeChart.activeAssetSymbol];
      const allSelected = Object.values(oiChangeActiveExpiries).every(
        isSelected => isSelected
      );
      Object.keys(oiChangeActiveExpiries).forEach(expiry => {
        oiChangeActiveExpiries[expiry] = !allSelected;
      });
      break;
    }

    // taker activity chart cases
    case UPDATE_TAKER_ACTIVITY_EXPIRIES:
      draft.takerActivityChart.expiries = action.payload;
      break;
    case UPDATE_TAKER_ACTIVITY_ACTIVE_ASSET:
      draft.takerActivityChart.activeAssetSymbol = action.payload;
      break;
    case UPDATE_TAKER_ACTIVITY_TIME_PERIOD:
      draft.takerActivityChart.activeTimePeriod = action.payload;
      break;
    case TOGGLE_TAKER_ACTIVITY_EXPIRY: {
      const { expiries, activeAssetSymbol } = draft.takerActivityChart;
      const timestamp = action.payload;
      draft.takerActivityChart.expiries[activeAssetSymbol][timestamp] =
        !expiries[activeAssetSymbol][timestamp];
      break;
    }
    case TOGGLE_TAKER_ACTIVITY_ALL_EXPIRIES: {
      const { expiries, activeAssetSymbol } = draft.takerActivityChart;
      const activeAssetExpiries = expiries[activeAssetSymbol];

      const allSelected = Object.values(activeAssetExpiries).every(
        isSelected => isSelected
      );
      Object.keys(activeAssetExpiries).forEach(expiry => {
        activeAssetExpiries[expiry] = !allSelected;
      });
      break;
    }
    case CHART_ACTIONS.GET_OPTIONS_TAKER_VOLUME.SUCCESS: {
      const { result } = action.result;
      draft.takerActivityChart.isLoading = false;

      // Function to transform object values to numbers
      const transformValueToNumber = mapObjIndexed(Number);
      const formattedData: TakerActivityFormattedData = {};

      // Iterate over each entry in the result object
      forEachObjIndexed((value, key) => {
        const { call_buy, call_sell, put_buy, put_sell } = value;

        const { CALL_BUY, CALL_SELL, PUT_BUY, PUT_SELL } =
          TAKER_ACTIVITY_CONTRACT_TYPE_MAPPING;

        // Format the data for each contract type and store it in formattedData
        formattedData[key] = {
          [CALL_BUY]: transformValueToNumber(call_buy),
          [CALL_SELL]: transformValueToNumber(call_sell),
          [PUT_BUY]: transformValueToNumber(put_buy),
          [PUT_SELL]: transformValueToNumber(put_sell),
        };
      }, result);

      draft.takerActivityChart.data = formattedData;
      break;
    }
    case CHART_ACTIONS.GET_OPTIONS_TAKER_VOLUME.FAIL: {
      draft.takerActivityChart.isLoading = false;
      draft.takerActivityChart.isError = false;
      break;
    }
    case CHART_ACTIONS.GET_OPTIONS_TAKER_VOLUME.LOAD: {
      draft.takerActivityChart.isLoading = true;
      break;
    }
    case UPDATE_TAKER_ACTIVITY_LOADING_DATA: {
      draft.takerActivityChart.isLoading = action.payload;
      break;
    }
    case UPDATE_TAKER_ACTIVITY_ERROR: {
      draft.takerActivityChart.isError = action.payload;
      break;
    }

    case UPDATE_TAKER_ACTIVITY_SOCKET_DATA: {
      const socketData = action.payload;
      draft.takerActivityChart.data = mergeDeepWith(
        (a, b) => Number(a) + Number(b),
        draft.takerActivityChart.data,
        socketData
      );
      break;
    }
    case UPDATE_TAKER_ACTIVITY_DATA: {
      draft.takerActivityChart.data = action.payload;
      break;
    }
    default:
      return draft;
  }
});

export default optionsAnalyticsReducer;
